import React, { useState, useEffect } from 'react';
import { useNotifier } from '../ui/Notifier';
import { clienteService, produtoService, seguradoraService } from '../../services/entitiesServices';
import { calculoService } from '../../services/calculoService';
import { cotacaoService } from '../../services/cotacaoService';

// Formatar CPF/CNPJ
const formatCpfCnpj = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
};

// Parser de moeda
const parseCurrency = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
};

// Formatar moeda para exibição
const formatCurrency = (value: number | string | undefined): string => {
  if (!value) return '0,00';
  const num = typeof value === 'string' ? parseCurrency(value) : value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Formatar data para input
const toInputDate = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
};

interface NovaCotacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export const NovaCotacaoModal: React.FC<NovaCotacaoModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const notifier = useNotifier();

  const [cpfCnpj, setCpfCnpj] = useState('');
  const [searchingCliente, setSearchingCliente] = useState(false);
  const [clienteFound, setClienteFound] = useState(false);
  const [showClienteFields, setShowClienteFields] = useState(false);

  // Dados do Cliente
  const [nomeCliente, setNomeCliente] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [telefoneCliente, setTelefoneCliente] = useState('');
  const [tipoPessoa, setTipoPessoa] = useState<'Física' | 'Jurídica'>('Física');

  // Dados do Cálculo
  const [produto, setProduto] = useState('');
  const [item, setItem] = useState('');
  const [zerokm, setZerokm] = useState(false);
  const [inicioVigencia, setInicioVigencia] = useState(toInputDate(new Date()));

  // Dados da Cotação
  const [seguradora, setSeguradora] = useState('');
  const [plano, setPlano] = useState('');
  const [tipoPlano, setTipoPlano] = useState('');
  const [premioTotal, setPremioTotal] = useState('0,00');
  const [percentualComis, setPercentualComis] = useState('0,00');
  const [valorComissao, setValorComissao] = useState('0,00');
  const [premioLiquido, setPremioLiquido] = useState('0,00');

  // Lista de Cotações
  const [cotacoes, setCotacoes] = useState<Array<{
    id: number;
    seguradora: string;
    seguradoraNome: string;
    plano: string;
    tipoPlano: string;
    premioTotal: number;
    percentualComis: number;
    valorComissao: number;
    premioLiquido: number;
  }>>([]);
  const [cotacaoIdCounter, setCotacaoIdCounter] = useState(1);

  // Opções
  const [produtos, setProdutos] = useState<Array<{ id: string; nome: string }>>([]);
  const [seguradoras, setSeguradoras] = useState<Array<{ id: string; nome: string }>>([]);
  const [loading, setLoading] = useState(false);

  // Carregar produtos e seguradoras
  const loadOptions = async () => {
    try {
      const [prodResponse, segResponse] = await Promise.all([
        produtoService.list(),
        seguradoraService.list(),
      ]);
      setProdutos(prodResponse.data || []);
      setSeguradoras(segResponse.data || []);
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);

  // Buscar cliente por CPF/CNPJ
  const searchCliente = async (cpfCnpjValue: string) => {
    const numbers = cpfCnpjValue.replace(/\D/g, '');
    if (numbers.length < 11) return;

    setSearchingCliente(true);
    try {
      const response = await clienteService.search(numbers);
      if (response.data && response.data.length > 0) {
        const cliente = response.data[0];
        setNomeCliente(cliente.nome || '');
        setEmailCliente(cliente.email || '');
        setTelefoneCliente(cliente.telefone || '');
        setTipoPessoa(cliente.tipoPessoa as 'Física' | 'Jurídica' || (numbers.length === 11 ? 'Física' : 'Jurídica'));
        setClienteFound(true);
        setShowClienteFields(true);
      } else {
        setClienteFound(false);
        setShowClienteFields(true);
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      setClienteFound(false);
      setShowClienteFields(true);
    } finally {
      setSearchingCliente(false);
    }
  };

  // Debounce da busca
  useEffect(() => {
    const numbers = cpfCnpj.replace(/\D/g, '');
    if (numbers.length === 11 || numbers.length === 14) {
      const timer = setTimeout(() => {
        searchCliente(cpfCnpj);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setClienteFound(false);
      setShowClienteFields(false);
    }
  }, [cpfCnpj]);

  // Calcular valores automaticamente
  const calculateValues = (field: string, value: string) => {
    const premio = field === 'premioTotal' ? parseCurrency(value) : parseCurrency(premioTotal);
    const percentual = field === 'percentualComis' ? parseCurrency(value) : parseCurrency(percentualComis);
    const comissao = field === 'valorComissao' ? parseCurrency(value) : parseCurrency(valorComissao);

    if (field === 'premioTotal' || field === 'percentualComis') {
      const novaComissao = (premio * percentual) / 100;
      setValorComissao(formatCurrency(novaComissao));
      setPremioLiquido(formatCurrency(premio - novaComissao));
    } else if (field === 'valorComissao') {
      setPremioLiquido(formatCurrency(premio - comissao));
    }
  };

  // Adicionar cotação à lista
  const adicionarCotacao = () => {
    if (!seguradora) {
      notifier.show({ message: 'Selecione uma seguradora', severity: 'error' });
      return;
    }

    const seguradoraNome = seguradoras.find(s => s.id === seguradora)?.nome || '';
    
    const novaCotacao = {
      id: cotacaoIdCounter,
      seguradora,
      seguradoraNome,
      plano,
      tipoPlano,
      premioTotal: parseCurrency(premioTotal),
      percentualComis: parseCurrency(percentualComis),
      valorComissao: parseCurrency(valorComissao),
      premioLiquido: parseCurrency(premioLiquido),
    };

    setCotacoes([...cotacoes, novaCotacao]);
    setCotacaoIdCounter(cotacaoIdCounter + 1);

    // Limpar campos da cotação
    setSeguradora('');
    setPlano('');
    setTipoPlano('');
    setPremioTotal('0,00');
    setPercentualComis('0,00');
    setValorComissao('0,00');
    setPremioLiquido('0,00');

    notifier.show({ message: 'Cotação adicionada à lista!', severity: 'success' });
  };

  // Remover cotação da lista
  const removerCotacao = (id: number) => {
    setCotacoes(cotacoes.filter(c => c.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cpfCnpj) {
      notifier.show({ message: 'Informe o CPF/CNPJ', severity: 'error' });
      return;
    }

    if (!nomeCliente) {
      notifier.show({ message: 'Informe o nome do cliente', severity: 'error' });
      return;
    }

    if (!produto) {
      notifier.show({ message: 'Selecione um produto', severity: 'error' });
      return;
    }

    // Verificar se há uma cotação preenchida no formulário mas não adicionada à lista
    const cotacaoAtualPreenchida = seguradora && premioTotal && parseCurrency(premioTotal) > 0;
    
    // Lista final de cotações a serem salvas
    let cotacoesParaSalvar = [...cotacoes];
    
    // Se há cotação preenchida mas não adicionada, adicionar automaticamente
    if (cotacaoAtualPreenchida) {
      const seguradoraSelecionada = seguradoras.find(s => s.id === seguradora);
      const novaCotacao = {
        id: cotacaoIdCounter,
        seguradora: seguradora,
        seguradoraNome: seguradoraSelecionada?.nome || '',
        plano: plano,
        tipoPlano: tipoPlano,
        premioTotal: parseCurrency(premioTotal),
        percentualComis: parseCurrency(percentualComis),
        valorComissao: parseCurrency(valorComissao),
        premioLiquido: parseCurrency(premioLiquido),
      };
      cotacoesParaSalvar.push(novaCotacao);
    }

    if (cotacoesParaSalvar.length === 0) {
      notifier.show({ message: 'Adicione pelo menos uma cotação', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      // 1. Criar/verificar cliente se necessário
      if (!clienteFound) {
        const clientePayload = {
          cpfCnpj: cpfCnpj.replace(/\D/g, ''),
          nome: nomeCliente,
          email: emailCliente || undefined,
          telefone: telefoneCliente || undefined,
          tipoPessoa: tipoPessoa,
        };
        await clienteService.create(clientePayload);
      }

      // 2. Criar o cálculo
      const calculoPayload = {
        cpfCnpj: cpfCnpj.replace(/\D/g, ''),
        nomeCliente: nomeCliente,
        produto: produto,
        item: item || undefined,
        zerokm: zerokm,
        inicioVigencia: inicioVigencia || undefined,
        email: emailCliente || undefined,
        telefone: telefoneCliente || undefined,
        tipoPessoa: tipoPessoa,
        situacao: 'Em análise',
        valor: Math.max(...cotacoesParaSalvar.map(c => c.premioTotal)),
        percentualComis: cotacoesParaSalvar[0]?.percentualComis || 0,
        valorComis: cotacoesParaSalvar[0]?.valorComissao || 0,
      };

      const calculoResponse = await calculoService.create(calculoPayload);
      
      if (!calculoResponse.success || !calculoResponse.data?.id) {
        throw new Error('Erro ao criar cálculo');
      }

      // 3. Criar todas as cotações vinculadas ao cálculo
      const cotacoesPromises = cotacoesParaSalvar.map(async (cot) => {
        const cotacaoPayload = {
          calculoId: calculoResponse.data.id,
          seguradora: cot.seguradora,
          plano: cot.plano || undefined,
          tipoPlano: cot.tipoPlano || undefined,
          premioTotal: cot.premioTotal,
          percentualComis: cot.percentualComis,
          valorComissao: cot.valorComissao,
          premioLiquido: cot.premioLiquido,
          selecionada: false,
        };

        return cotacaoService.create(cotacaoPayload);
      });

      await Promise.all(cotacoesPromises);
      
      notifier.show({ message: `Cálculo e ${cotacoesParaSalvar.length} cotação(ões) criados com sucesso!`, severity: 'success' });
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      notifier.show({ message: error.message || 'Erro ao salvar cálculo e cotação', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCpfCnpj('');
    setNomeCliente('');
    setEmailCliente('');
    setTelefoneCliente('');
    setClienteFound(false);
    setShowClienteFields(false);
    setProduto('');
    setItem('');
    setZerokm(false);
    setInicioVigencia(toInputDate(new Date()));
    setSeguradora('');
    setPlano('');
    setTipoPlano('');
    setPremioTotal('0,00');
    setPercentualComis('0,00');
    setValorComissao('0,00');
    setPremioLiquido('0,00');
    setCotacoes([]);
    setCotacaoIdCounter(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Nova Cotação</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Busca de Cliente */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Buscar Cliente
            </h3>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                CPF/CNPJ <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={cpfCnpj}
                  onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  required
                />
                {searchingCliente && (
                  <div className="absolute right-3 top-2">
                    <svg className="animate-spin h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                {clienteFound && !searchingCliente && (
                  <div className="absolute right-3 top-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dados do Cliente (expandível) */}
          {(clienteFound || showClienteFields) && (
            <div className="border-t pt-6 animate-in slide-in-from-top duration-300">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {clienteFound ? 'Dados do Cliente' : 'Cadastrar Novo Cliente'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nome */}
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={nomeCliente}
                    onChange={(e) => setNomeCliente(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${clienteFound ? 'bg-gray-50' : ''}`}
                    readOnly={clienteFound}
                    required
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">E-mail</label>
                  <input
                    type="email"
                    value={emailCliente}
                    onChange={(e) => setEmailCliente(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${clienteFound ? 'bg-gray-50' : ''}`}
                    readOnly={clienteFound}
                  />
                </div>

                {/* Telefone */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Telefone</label>
                  <input
                    type="text"
                    value={telefoneCliente}
                    onChange={(e) => setTelefoneCliente(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${clienteFound ? 'bg-gray-50' : ''}`}
                    placeholder="(00) 00000-0000"
                    readOnly={clienteFound}
                  />
                </div>

                {/* Tipo Pessoa */}
                {!clienteFound && (
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Tipo de Pessoa</label>
                    <div className="flex gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="Física"
                          checked={tipoPessoa === 'Física'}
                          onChange={(e) => setTipoPessoa(e.target.value as 'Física')}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Pessoa Física</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="Jurídica"
                          checked={tipoPessoa === 'Jurídica'}
                          onChange={(e) => setTipoPessoa(e.target.value as 'Jurídica')}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Pessoa Jurídica</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {!clienteFound && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800">
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    O cliente será cadastrado automaticamente ao salvar a cotação.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Dados do Cálculo */}
          {(clienteFound || showClienteFields) && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Dados do Cálculo
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Produto */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Produto <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={produto}
                    onChange={(e) => setProduto(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione...</option>
                    {produtos.map((prod) => (
                      <option key={prod.id} value={prod.nome}>
                        {prod.nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Item</label>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => setItem(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Honda Civic, Casa, etc."
                  />
                </div>

                {/* Início Vigência */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Início Vigência</label>
                  <input
                    type="date"
                    value={inicioVigencia}
                    onChange={(e) => setInicioVigencia(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* 0 KM */}
                <div className="flex items-center gap-2 mt-8">
                  <input
                    type="checkbox"
                    id="zerokm"
                    checked={zerokm}
                    onChange={(e) => setZerokm(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="zerokm" className="text-sm font-medium text-gray-700">
                    0 KM
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Dados da Cotação */}
          {(clienteFound || showClienteFields) && produto && (
            <div className="border-t pt-6 animate-in slide-in-from-top duration-300">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Dados da Cotação
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Seguradora */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Seguradora <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={seguradora}
                    onChange={(e) => setSeguradora(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione...</option>
                    {seguradoras.map((seg) => (
                      <option key={seg.id} value={seg.nome}>
                        {seg.nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Plano */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Plano</label>
                  <input
                    type="text"
                    value={plano}
                    onChange={(e) => setPlano(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Básico, Completo..."
                  />
                </div>

                {/* Tipo de Plano */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Tipo de Plano</label>
                  <input
                    type="text"
                    value={tipoPlano}
                    onChange={(e) => setTipoPlano(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Anual, Semestral..."
                  />
                </div>

                {/* Prêmio Total */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Prêmio Total</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="text"
                      value={premioTotal}
                      onChange={(e) => {
                        setPremioTotal(e.target.value);
                        calculateValues('premioTotal', e.target.value);
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="0,00"
                    />
                  </div>
                </div>

                {/* Percentual Comissão */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Comissão (%)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={percentualComis}
                      onChange={(e) => {
                        setPercentualComis(e.target.value);
                        calculateValues('percentualComis', e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="0,00"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">%</span>
                  </div>
                </div>

                {/* Valor Comissão */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Valor Comissão</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="text"
                      value={valorComissao}
                      onChange={(e) => {
                        setValorComissao(e.target.value);
                        calculateValues('valorComissao', e.target.value);
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="0,00"
                    />
                  </div>
                </div>

                {/* Prêmio Líquido */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Prêmio Líquido</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="text"
                      value={premioLiquido}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      placeholder="0,00"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Botão Adicionar Cotação */}
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={adicionarCotacao}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  + Adicionar Cotação
                </button>
              </div>

              {/* Lista de Cotações Adicionadas */}
              {cotacoes.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Cotações Adicionadas ({cotacoes.length})</h4>
                  <div className="space-y-2">
                    {cotacoes.map((cot) => (
                      <div key={cot.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{cot.seguradoraNome}</div>
                          <div className="text-xs text-gray-500 space-x-2">
                            {cot.plano && <span>Plano: {cot.plano}</span>}
                            {cot.tipoPlano && <span>• Tipo: {cot.tipoPlano}</span>}
                            <span>• Prêmio: R$ {formatCurrency(cot.premioTotal)}</span>
                            <span>• Comissão: {formatCurrency(cot.percentualComis)}%</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removerCotacao(cot.id)}
                          className="ml-3 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Remover"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !nomeCliente || !cpfCnpj || !produto || (cotacoes.length === 0 && !seguradora)}
            >
              {loading ? 'Salvando...' : `Salvar Cálculo${cotacoes.length > 0 || seguradora ? ` (${cotacoes.length + (seguradora ? 1 : 0)} cotação${cotacoes.length + (seguradora ? 1 : 0) !== 1 ? 'ões' : ''})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
